import { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { BASE_URL } from "../baseurl";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function AdminDashboard() {
  const [salesData, setSalesData] = useState([]);
  const [ordersData, setOrdersData] = useState([]);
  const [topSellingItems, setTopSellingItems] = useState([]);
  const [totals, setTotals] = useState({ totalSales: 0, totalOrders: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("adminToken");
        const res = await fetch(`${BASE_URL}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Failed to fetch dashboard stats");

        setSalesData(
          data.salesByMonth.map((entry) => ({
            month: MONTH_NAMES[entry._id.month - 1],
            sales: entry.sales,
          }))
        );

        setOrdersData(
          data.ordersByDay.map((entry) => ({
            day: DAY_NAMES[new Date(entry._id.year, entry._id.month - 1, entry._id.day).getDay()],
            orders: entry.orders,
          }))
        );

        setTopSellingItems(data.topSellingItems || []);
        setTotals(data.totals);
      } catch (err) {
        console.error("Fetch dashboard stats error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const stats = [
    {
      label: "Total Sales",
      value: `$${totals.totalSales.toLocaleString()}`,
      icon: "sales",
    },
    {
      label: "Orders",
      value: totals.totalOrders.toString(),
      icon: "orders",
    },
  ];

  const icons = {
    sales: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 17l6-6 4 4 8-8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15 7h6v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    profit: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
        <path d="M12 7v10M9 9.5a2.5 2.5 0 012.5-1.5h1a2 2 0 010 4h-1a2 2 0 000 4h1a2.5 2.5 0 002.5-1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    orders: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 3h2l2.4 12.4a2 2 0 002 1.6h8.4a2 2 0 002-1.6L21 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="9" cy="21" r="1" fill="currentColor" />
        <circle cx="18" cy="21" r="1" fill="currentColor" />
      </svg>
    ),
    refund: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 12a9 9 0 1115.5 6.4M3 12v5M3 12h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  };

  if (loading) {
    return (
      <div className="w-full text-center text-gray-500 py-24">
        Loading dashboard...
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full text-center text-gray-500 py-24">
        Failed to load dashboard: {error}
      </div>
    );
  }

  const maxCount = topSellingItems.length > 0 ? topSellingItems[0].count : 1;

  return (
    <div className="w-full">
      <div className="mb-8">
        <h1 className="text-gray-800 font-semibold text-2xl">Dashboard</h1>
        <p className="text-gray-500 text-sm mt-1">
          Overview of your store's performance
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="bg-white border border-gray-200 rounded-lg p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-md bg-teal-50 text-teal-600 flex items-center justify-center">
                {icons[stat.icon]}
              </div>
              {stat.change && (
                <span
                  className={`text-xs font-medium px-2 py-1 rounded-full ${
                    stat.positive
                      ? "bg-teal-50 text-teal-600"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {stat.change}
                </span>
              )}
            </div>
            <p className="text-gray-500 text-xs">{stat.label}</p>
            <p className="text-gray-800 font-semibold text-xl mt-1">
              {stat.value}
            </p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-gray-800 font-semibold text-base mb-1">
            Sales
          </h2>
          <p className="text-gray-400 text-xs mb-6">Last 7 months</p>

          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={salesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: "#6b7280", fontSize: 12 }} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} />
              <YAxis tick={{ fill: "#6b7280", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  fontSize: "13px",
                }}
              />
              <Legend wrapperStyle={{ fontSize: "13px" }} />
              <Line type="monotone" dataKey="sales" stroke="#14b8a6" strokeWidth={2.5} dot={{ r: 3 }} name="Sales ($)" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-gray-800 font-semibold text-base mb-1">
            Top Selling Items
          </h2>
          <p className="text-gray-400 text-xs mb-6">All time</p>

          {topSellingItems.length === 0 ? (
            <p className="text-gray-400 text-sm">No sales data yet.</p>
          ) : (
            <div className="flex flex-col gap-4">
              {topSellingItems.map((item, index) => (
                <div key={item.itemId}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-700 font-medium truncate pr-2">
                      {index + 1}. {item.title}
                    </span>
                    <span className="text-gray-500 text-xs whitespace-nowrap">
                      {item.count} order{item.count !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className="bg-teal-500 h-2 rounded-full"
                      style={{ width: `${(item.count / maxCount) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-gray-800 font-semibold text-base mb-1">
          Orders This Week
        </h2>
        <p className="text-gray-400 text-xs mb-6">Daily order count</p>

        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={ordersData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="day" tick={{ fill: "#6b7280", fontSize: 12 }} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} />
            <YAxis tick={{ fill: "#6b7280", fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{
                backgroundColor: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "13px",
              }}
            />
            <Bar dataKey="orders" fill="#14b8a6" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}